import React, { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Collapse,
    IconButton,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import { REFUNDED_TRANSFER_TYPES } from '../constants/game';

/**
 * TransferRound component — toont een transferronde blok in de admin UI.
 * Gebaseerd op het BlindPoolRound patroon.
 *
 * Props:
 * - round: het transferRound document (id, roundNumber, status, storageId, stageName, matchName, participants, ...)
 * - liveParticipants: array van live participant docs (met id, name)
 * - transfers: array van alle auctionTransfers documenten
 * - computedTransfersLeftMap: Map<uid, number> — berekende transfersLeft per deelnemer op basis van werkelijke transfers
 * - onCloseRound: callback(roundId)
 * - onDeleteRound: callback(roundId)
 * - onDeleteTransfer: callback(transferId, participantUid) — verwijdert een transfer en corrigeert transfersLeft
 */
function TransferRound({ round, liveParticipants = [], transfers = [], computedTransfersLeftMap = new Map(), onCloseRound, onDeleteRound, onDeleteTransfer }) {
    const [showTransfers, setShowTransfers] = useState(false);
    const [confirmDeleteTransfer, setConfirmDeleteTransfer] = useState(null);
    // Map live participant data voor snelle lookup
    const participantMap = React.useMemo(() => {
        const map = new Map();
        liveParticipants.forEach(p => map.set(p.id || p.uid, p));
        return map;
    }, [liveParticipants]);

    // Filter transfers die binnen het tijdsvenster van deze ronde vallen
    const roundTransfers = React.useMemo(() => {
        if (!round.createdAt) return [];
        const startSeconds = round.createdAt.seconds || 0;
        const endSeconds = round.closedAt?.seconds || null;

        return transfers
            .filter(t => {
                if (!t.timestamp?.seconds) return false;
                const ts = t.timestamp.seconds;
                if (ts < startSeconds) return false;
                if (endSeconds && ts > endSeconds) return false;
                return true;
            })
            .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
    }, [transfers, round.createdAt, round.closedAt]);

    // Bereken per geselecteerde deelnemer hoeveel transfers ze gedaan hebben IN DEZE RONDE
    const participantRows = React.useMemo(() => {
        if (!round.participants) return [];

        // Types die niet meetellen als verbruikte transfer
        const excludedTypes = ['coach-change', 'Draft'];

        return Object.entries(round.participants)
            .map(([participantId, data]) => {
                // Tel alleen transfers die in DEZE ronde vallen voor deze deelnemer
                const incomingInRound = roundTransfers.filter(
                    t => t.to === participantId && !excludedTypes.includes(t.type)
                ).length;
                const nonClInRound = roundTransfers.filter(
                    t => t.from === participantId && REFUNDED_TRANSFER_TYPES.includes(t.type)
                ).length;
                const transfersDone = Math.max(0, incomingInRound - nonClInRound);

                const remainingInRound = Math.max(0, data.allowance - transfersDone);

                return {
                    participantId,
                    name: data.name,
                    transfersLeftAtStart: data.transfersLeftAtStart,
                    allowance: data.allowance,
                    transfersDone,
                    remainingInRound
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [round.participants, roundTransfers]);

    const allDone = participantRows.every(p => p.remainingInRound === 0);

    return (
        <Box sx={{ mb: 4 }} component={Paper} p={3}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6">
                        Transferronde {round.roundNumber}
                    </Typography>
                    <Chip
                        label={round.status === 'open' ? 'Open' : 'Gesloten'}
                        color={round.status === 'open' ? 'success' : 'default'}
                        size="small"
                    />
                    {round.matchName && (
                        <Typography variant="body2" color="text.secondary">
                            {round.matchName}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {round.status === 'open' && (
                        <Button
                            variant="contained"
                            onClick={() => onCloseRound(round.id)}
                            size="small"
                        >
                            Ronde Sluiten
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => onDeleteRound(round.id)}
                        size="small"
                    >
                        Verwijderen
                    </Button>
                </Box>
            </Box>

            {/* Info over alle rondes afgewerkt */}
            {round.status === 'open' && allDone && participantRows.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Chip label="Alle deelnemers hebben hun transfers gebruikt" color="info" size="small" />
                </Box>
            )}

            {/* Tabel met deelnemers */}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Deelnemer</TableCell>
                            <TableCell align="right">Transfers totaal resterend</TableCell>
                            <TableCell align="right">Toegestaan in ronde</TableCell>
                            <TableCell align="right">Gebruikt in ronde</TableCell>
                            <TableCell align="right">Resterend in ronde</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {participantRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Geen deelnemers geselecteerd voor deze ronde.
                                </TableCell>
                            </TableRow>
                        ) : (
                            participantRows.map(row => (
                                <TableRow key={row.participantId} hover>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell align="right">{row.transfersLeftAtStart}</TableCell>
                                    <TableCell align="right">{row.allowance}</TableCell>
                                    <TableCell align="right">{row.transfersDone}</TableCell>
                                    <TableCell align="right">
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontWeight: row.remainingInRound > 0 ? 'bold' : 'normal',
                                                color: row.remainingInRound > 0 ? 'success.main' : 'text.secondary'
                                            }}
                                        >
                                            {row.remainingInRound}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Transfers in deze ronde */}
            <Box sx={{ mt: 2 }}>
                <Button
                    size="small"
                    onClick={() => setShowTransfers(!showTransfers)}
                    endIcon={showTransfers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    Transfers in deze ronde ({roundTransfers.length})
                </Button>
                <Collapse in={showTransfers}>
                    {roundTransfers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Geen transfers gevonden in deze periode.
                        </Typography>
                    ) : (
                        <TableContainer sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Datum/Tijd</TableCell>
                                        <TableCell>Speler</TableCell>
                                        <TableCell>Van</TableCell>
                                        <TableCell>Naar</TableCell>
                                        <TableCell align="right">Bedrag</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="center">Actie</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {roundTransfers.map(transfer => {
                                        const fromName = participantMap.get(transfer.from)?.name || transfer.from;
                                        const toName = participantMap.get(transfer.to)?.name || transfer.to;
                                        const dateStr = transfer.timestamp
                                            ? new Date(transfer.timestamp.seconds * 1000).toLocaleString('nl-NL', {
                                                day: '2-digit', month: '2-digit', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit', second: '2-digit'
                                            })
                                            : '';

                                        return (
                                            <TableRow key={transfer.id} hover>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateStr}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {transfer.photoUrl && (
                                                            <Avatar src={transfer.photoUrl} alt={transfer.playerName} sx={{ width: 28, height: 28 }} />
                                                        )}
                                                        {transfer.playerName}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{fromName}</TableCell>
                                                <TableCell>{toName}</TableCell>
                                                <TableCell align="right">€{transfer.amount}</TableCell>
                                                <TableCell>
                                                    <Chip label={transfer.type || 'Onbekend'} size="small" />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setConfirmDeleteTransfer(transfer)}
                                                        title="Transfer verwijderen"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Collapse>
            </Box>

            {/* Bevestigings-dialoog voor transfer verwijderen */}
            <Dialog open={!!confirmDeleteTransfer} onClose={() => setConfirmDeleteTransfer(null)}>
                <DialogTitle>Transfer Verwijderen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de volgende transfer wilt verwijderen?
                    </DialogContentText>
                    {confirmDeleteTransfer && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="body2"><strong>Speler:</strong> {confirmDeleteTransfer.playerName}</Typography>
                            <Typography variant="body2"><strong>Van:</strong> {participantMap.get(confirmDeleteTransfer.from)?.name || confirmDeleteTransfer.from}</Typography>
                            <Typography variant="body2"><strong>Naar:</strong> {participantMap.get(confirmDeleteTransfer.to)?.name || confirmDeleteTransfer.to}</Typography>
                        </Box>
                    )}
                    {confirmDeleteTransfer && participantMap.has(confirmDeleteTransfer.to) && (
                        <DialogContentText sx={{ mt: 2 }}>
                            Het aantal resterende transfers van de betreffende deelnemer wordt automatisch gecorrigeerd (+1).
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteTransfer(null)}>Annuleren</Button>
                    <Button
                        color="error"
                        onClick={() => {
                            if (confirmDeleteTransfer && onDeleteTransfer) {
                                // Alleen transfersLeft corrigeren bij inkomende transfers (to = deelnemer)
                                const participantUid = participantMap.has(confirmDeleteTransfer.to)
                                    ? confirmDeleteTransfer.to
                                    : null;
                                onDeleteTransfer(confirmDeleteTransfer.id, participantUid);
                            }
                            setConfirmDeleteTransfer(null);
                        }}
                    >
                        Verwijderen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default TransferRound;
